import React, { useRef, useEffect } from "react";
import { siteConfig } from "../siteConfig";
import Reveal from "./Reveal";

const { colors } = siteConfig;

/* ─── Slider core ───────────────────────────────────────────────────── */
const BeforeAfterSlider = ({
  beforeImage,
  afterImage,
  beforeLabel = "Before",
  afterLabel = "After",
}) => {
  const containerRef = useRef(null);
  const afterDivRef  = useRef(null);
  const dividerRef   = useRef(null);
  const handleRef    = useRef(null);
  const dragging     = useRef(false);

  // Directly write position to DOM — zero React re-renders during drag
  const applyPos = (pos) => {
    pos = Math.min(Math.max(pos, 0), 100);
    if (afterDivRef.current)
      afterDivRef.current.style.clipPath = `inset(0 0 0 ${pos}%)`;
    if (dividerRef.current)  dividerRef.current.style.left  = `${pos}%`;
    if (handleRef.current)   handleRef.current.style.left   = `${pos}%`;
  };

  const setTransition = (val) => {
    if (afterDivRef.current) afterDivRef.current.style.transition = val ? `clip-path ${val}` : "none";
    if (dividerRef.current)  dividerRef.current.style.transition  = val ? `left ${val}`      : "none";
    if (handleRef.current)   handleRef.current.style.transition   = val ? `left ${val}`      : "none";
  };

  const calcPos = (clientX) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return 50;
    return Math.min(Math.max(((clientX - rect.left) / rect.width) * 100, 0), 100);
  };

  // Global move/end listeners — always active, guarded by dragging ref
  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      applyPos(calcPos(e.touches ? e.touches[0].clientX : e.clientX));
    };
    const onEnd = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup",   onEnd);
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend",  onEnd);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup",   onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend",  onEnd);
    };
  }, []); // runs once — no deps needed since everything uses refs

  const startDrag = (e) => {
    e.preventDefault();
    setTransition(null); // kill transition synchronously before first move
    dragging.current = true;
  };

  const snapTo = (target) => {
    setTransition("0.4s ease");
    applyPos(target);
    setTimeout(() => setTransition(null), 450);
  };

  return (
    <div
      ref={containerRef}
      className="relative select-none overflow-hidden rounded-2xl shadow-2xl"
      style={{ cursor: "ew-resize", userSelect: "none" }}
    >
      {/* ── Base: Before image ── */}
      <img
        src={beforeImage}
        alt="Before"
        draggable={false}
        className="block w-full"
        style={{ maxHeight: "520px", objectFit: "cover", width: "100%", display: "block" }}
      />

      {/* ── Overlay: After image ── */}
      <div
        ref={afterDivRef}
        className="absolute inset-0"
        style={{ clipPath: "inset(0 0 0 50%)", pointerEvents: "none" }}
      >
        <img
          src={afterImage}
          alt="After"
          draggable={false}
          className="w-full h-full object-cover block"
        />
      </div>

      {/* ── Divider line ── */}
      <div
        ref={dividerRef}
        className="absolute top-0 bottom-0 w-0.5 bg-white z-10"
        style={{ left: "50%", transform: "translateX(-50%)", pointerEvents: "none" }}
      />

      {/* ── Drag handle ── */}
      <div
        ref={handleRef}
        className="absolute top-1/2 z-20 rounded-full bg-white flex items-center justify-center cursor-col-resize"
        style={{
          width: 46,
          height: 46,
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
        }}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
      >
        <svg width="22" height="16" viewBox="0 0 22 16" fill="none">
          <path d="M7 8L3 5l4-3" stroke="#444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M15 8l4-3-4-3" stroke="#444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="3" y1="8" x2="19" y2="8" stroke="#444" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      </div>

      {/* ── Before button ── */}
      <button
        className="absolute bottom-4 left-4 z-20 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full text-white transition-transform hover:scale-105"
        style={{ background: "rgba(0,0,0,0.60)", backdropFilter: "blur(6px)" }}
        onClick={() => snapTo(85)}
      >
        {beforeLabel}
      </button>

      {/* ── After button ── */}
      <button
        className="absolute bottom-4 right-4 z-20 text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full text-white transition-transform hover:scale-105"
        style={{ backgroundColor: colors.primary }}
        onClick={() => snapTo(15)}
      >
        {afterLabel}
      </button>
    </div>
  );
};

/* ─── Homepage section wrapper ──────────────────────────────────────── */
export const BeforeAfterSection = () => {
  const { beforeAfter } = siteConfig;
  if (!beforeAfter?.before || !beforeAfter?.after) return null;

  return (
    <section style={{ backgroundColor: colors.dark }}>
      <div className="w-full max-w-5xl mx-auto px-5 md:px-8 py-14 md:py-20">

        {/* Heading */}
        <Reveal as="div" className="text-center mb-8">
          <div
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest mb-3"
            style={{ color: colors.primary }}
          >
            <span className="w-4 h-px" style={{ backgroundColor: colors.primary }} />
            Real Transformations
            <span className="w-4 h-px" style={{ backgroundColor: colors.primary }} />
          </div>
          <h2
            className="font-display font-black leading-tight text-white mb-3"
            style={{ fontSize: "clamp(1.75rem,3.75vw,2.75rem)" }}
          >
            {beforeAfter.heading || "Before & After"}
          </h2>
          {beforeAfter.sub && (
            <p className="text-white/60 max-w-xl mx-auto text-base leading-relaxed">
              {beforeAfter.sub}
            </p>
          )}
        </Reveal>

        {/* Slider */}
        <Reveal as="div">
          <BeforeAfterSlider
            beforeImage={beforeAfter.before}
            afterImage={beforeAfter.after}
          />
          <p className="text-center text-white/40 text-xs mt-4 tracking-wide">
            Drag the handle or tap Before / After to compare
          </p>
        </Reveal>

      </div>
    </section>
  );
};

export default BeforeAfterSlider;
